/**
 * optionsSuggest.service — strategy suggester for the Options Strategy tab
 * inside OrderConfirmDialog (Round 67b).
 *
 * Backed by POST /v1/options/suggest (rank list) and
 *           POST /v1/options/suggest/execute (persist as OptionBasket).
 *
 * The dialog opens at most a few times per session; no client-side cache.
 */
import api from './api';

export type StrategyDirection = 'LONG' | 'SHORT' | 'NEUTRAL';
export type RiskShape = 'debit' | 'credit' | 'undefined';

export interface SuggestedLeg {
  action: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  expiry: string;
  symbol: string;
  quantity: number;
  ltp: number;
  bid: number | null;
  ask: number | null;
  bidAskSpread: number;
  costPerLeg: number;
}

export interface SuggestedStrategy {
  id: string;
  name: string;
  direction: StrategyDirection;
  riskShape: RiskShape;
  matchesDirection: boolean;
  fitScore: number;
  rationale: string;
  greeks: { delta: number; theta: number; vega: number };
  legs: SuggestedLeg[];
  netPremium: number;
  marginEstimate: number;
  /** BROKER = real Zerodha SPAN basket margin; ESTIMATE = in-app heuristic. */
  marginSource?: 'BROKER' | 'ESTIMATE';
  maxProfit: number;
  maxLoss: number;
  breakEvens: number[];
  /**
   * @deprecated Not a probability — the fraction of the simulated SPOT RANGE that is profitable,
   * every price weighted equally. A long strangle scores 96% on it. Use `probabilityOfProfit`.
   */
  winProbability: number;
  /** P(profit) under the option market's own implied distribution. Null when it cannot be computed. */
  probabilityOfProfit: number | null;
  expectedPayoff: number | null;
  profitUnbounded?: boolean;
  lossUnbounded?: boolean;
  oneSdMovePct?: number | null;
  roi: number;
  warnings: string[];
  fits: string[];
  payoffData: { spotRange: number[]; expiry: number[]; today: number[] };
}

export interface ConsideredStrategy {
  id: string;
  name: string;
  direction: StrategyDirection;
  reason: string;
}

export interface SuggestResponse {
  ok: boolean;
  reason?: string;
  /** True when the backend just auto-registered this underlying in the FNO
   *  module — first option-chain snapshot lands in ~30-60s. UI should
   *  auto-retry every ~20s instead of showing a hard error. */
  bootstrapping?: boolean;
  /** Strategies that were rejected during candidate build. Lets the UI render
   *  a transparent "we tried these N, here's why they didn't fit" panel when
   *  the main lists are empty. */
  considered?: ConsideredStrategy[];
  underlying?: {
    id: number; symbol: string; displayName?: string; feedSource: string;
    lotSize: number; spotPrice: number; expiry: string;
    atmStrike: number; strikeStep: number;
    ivRank30d?: number; ivRank52w?: number; hv30d?: number; atmIv?: number;
  };
  strategies: SuggestedStrategy[];
}

export interface SuggestRequest {
  underlyingSymbol: string;
  direction: 'LONG' | 'SHORT';
  riskInr: number;
  availableMarginInr: number;
  horizonDays?: number;
  allowUndefinedRisk?: boolean;
  exchange?: string;
}

export const optionsSuggestService = {
  async suggest(req: SuggestRequest): Promise<SuggestResponse> {
    try {
      const r = await api.post<{ success: boolean; data: SuggestResponse }>('/options/suggest', req, {
        headers: { _suppressToast: 'true' },
      });
      return r.data?.data || { ok: false, reason: 'No response', strategies: [] };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message || 'Suggest failed', strategies: [] };
    }
  },

  async execute(req: SuggestRequest & { strategyId: string; lots?: number }): Promise<{ ok: boolean; basketId?: number; reason?: string }> {
    try {
      const r = await api.post<{ success: boolean; data: { basketId: number; strategy: string; lots: number } }>('/options/suggest/execute', req);
      return { ok: !!r.data?.success, basketId: r.data?.data?.basketId };
    } catch (e: any) {
      return { ok: false, reason: e?.response?.data?.error?.message || e?.message || 'Execute failed' };
    }
  },
};
